/**
 * Settings → Benefits → Plans (admin).
 *
 * Grid of configured plans with create / edit / deactivate. Each card
 * shows plan name, carrier, type badge, configured tier count, and
 * employee-cost range so the admin can scan at a glance.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import {
  createPlan,
  deletePlan,
  listPlans,
  updatePlan,
  type BenefitPlan,
  type PlanType,
  type Tier,
  type TierRate,
} from "@/api/benefits";

const PLAN_TYPES: { value: PlanType; label: string }[] = [
  { value: "medical", label: "Medical" },
  { value: "dental", label: "Dental" },
  { value: "vision", label: "Vision" },
  { value: "life", label: "Life" },
  { value: "disability_short", label: "Disability — Short Term" },
  { value: "disability_long", label: "Disability — Long Term" },
  { value: "accident", label: "Accident" },
  { value: "critical_illness", label: "Critical Illness" },
  { value: "hsa", label: "HSA" },
  { value: "fsa", label: "FSA" },
  { value: "retirement_401k", label: "401(k)" },
];

const TIER_LABELS: Record<Tier, string> = {
  employee_only: "Employee Only",
  employee_spouse: "Employee + Spouse",
  employee_children: "Employee + Children",
  family: "Family",
  waived: "Waived",
};

const DEFAULT_TIERS: Tier[] = [
  "employee_only",
  "employee_spouse",
  "employee_children",
  "family",
];

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });
}

export function BenefitsPlansPage() {
  const { addToast } = useToast();
  const [plans, setPlans] = useState<BenefitPlan[] | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BenefitPlan | null>(null);

  const reload = async () => {
    try {
      setPlans(await listPlans(true));
    } catch {
      addToast("error", "Failed to load plans");
      setPlans([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (p: BenefitPlan) => {
    setEditing(p);
    setOpen(true);
  };

  const handleDeactivate = async (p: BenefitPlan) => {
    if (!confirm(`Deactivate "${p.name}"? Existing elections remain.`)) return;
    try {
      await deletePlan(p.id);
      addToast("success", "Plan deactivated");
      reload();
    } catch {
      addToast("error", "Couldn't deactivate plan");
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Benefit Plans"
        subtitle="Configure the medical, dental, vision, and other plans available to employees during enrollment"
      />

      <Card>
        <CardHeader>
          <CardTitle>All plans</CardTitle>
          <Button size="sm" onClick={openNew}>
            <Plus className="h-4 w-4" />
            Add plan
          </Button>
        </CardHeader>

        {!plans ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : plans.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No plans configured yet. Add your first plan to start enrollment.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                onEdit={() => openEdit(p)}
                onDeactivate={() => handleDeactivate(p)}
              />
            ))}
          </div>
        )}
      </Card>

      <PlanModal
        isOpen={open}
        onClose={() => setOpen(false)}
        editing={editing}
        onSaved={async () => {
          setOpen(false);
          await reload();
        }}
      />
    </motion.div>
  );
}

function PlanCard({
  plan,
  onEdit,
  onDeactivate,
}: {
  plan: BenefitPlan;
  onEdit: () => void;
  onDeactivate: () => void;
}) {
  const empCosts = plan.tier_rates
    .filter((t) => t.tier !== "waived")
    .map((t) => t.employee_cost);
  const min = empCosts.length ? Math.min(...empCosts) : 0;
  const max = empCosts.length ? Math.max(...empCosts) : 0;
  return (
    <div className="rounded-md border border-border/60 bg-surface/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">
            {plan.name}
          </p>
          <p className="text-xs text-text-muted truncate">
            {plan.carrier_name}
          </p>
        </div>
        <Badge variant={plan.is_active ? "success" : "default"}>
          {plan.is_active ? "Active" : "Inactive"}
        </Badge>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Badge variant="info">
          {PLAN_TYPES.find((t) => t.value === plan.plan_type)?.label
            || plan.plan_type}
        </Badge>
        <span className="text-xs text-text-muted">
          {plan.tier_rates.length} tier
          {plan.tier_rates.length === 1 ? "" : "s"}
        </span>
      </div>
      {empCosts.length > 0 && (
        <p className="text-xs text-text-muted mt-2">
          Employee cost: {fmt(min)} – {fmt(max)}/mo
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onEdit}>
          Edit
        </Button>
        {plan.is_active && (
          <Button size="sm" variant="ghost" onClick={onDeactivate}>
            <Trash2 className="h-3.5 w-3.5 text-danger" />
            Deactivate
          </Button>
        )}
      </div>
    </div>
  );
}

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  editing: BenefitPlan | null;
  onSaved: () => void;
}

function PlanModal({ isOpen, onClose, editing, onSaved }: PlanModalProps) {
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [carrier, setCarrier] = useState("");
  const [planType, setPlanType] = useState<PlanType>("medical");
  const [tierRates, setTierRates] = useState<TierRate[]>(
    DEFAULT_TIERS.map((t) => ({ tier: t, employer_cost: 0, employee_cost: 0 })),
  );
  const [details, setDetails] = useState({
    deductible: "",
    out_of_pocket_max: "",
    copay: "",
    coinsurance: "",
  });
  const [docName, setDocName] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setName(editing.name);
      setCarrier(editing.carrier_name);
      setPlanType(editing.plan_type);
      // Re-pad tier_rates to the four standard tiers so the editor
      // shows all of them even if some had zero cost.
      const byTier = new Map<Tier, TierRate>();
      editing.tier_rates.forEach((r) => byTier.set(r.tier, r));
      setTierRates(
        DEFAULT_TIERS.map(
          (t) =>
            byTier.get(t) || {
              tier: t,
              employer_cost: 0,
              employee_cost: 0,
            },
        ),
      );
      const d = (editing.details || {}) as Record<string, string>;
      setDetails({
        deductible: String(d.deductible ?? ""),
        out_of_pocket_max: String(d.out_of_pocket_max ?? ""),
        copay: String(d.copay ?? ""),
        coinsurance: String(d.coinsurance ?? ""),
      });
      setDocName(editing.document_name || "");
      setDocUrl(editing.document_url || "");
      setIsActive(editing.is_active);
      setStart(editing.effective_start || "");
      setEnd(editing.effective_end || "");
    } else {
      setName("");
      setCarrier("");
      setPlanType("medical");
      setTierRates(
        DEFAULT_TIERS.map((t) => ({
          tier: t,
          employer_cost: 0,
          employee_cost: 0,
        })),
      );
      setDetails({
        deductible: "",
        out_of_pocket_max: "",
        copay: "",
        coinsurance: "",
      });
      setDocName("");
      setDocUrl("");
      setIsActive(true);
      setStart("");
      setEnd("");
    }
  }, [isOpen, editing]);

  const updateTier = (
    tier: Tier,
    field: "employer_cost" | "employee_cost",
    value: string,
  ) => {
    setTierRates((rates) =>
      rates.map((r) =>
        r.tier === tier ? { ...r, [field]: Number(value || 0) } : r,
      ),
    );
  };

  const save = async () => {
    if (!name.trim() || !carrier.trim()) {
      addToast("error", "Name and carrier are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        carrier_name: carrier.trim(),
        plan_type: planType,
        tier_rates: tierRates.filter(
          (r) => r.employer_cost > 0 || r.employee_cost > 0,
        ),
        details,
        document_url: docUrl.trim() || null,
        document_name: docName.trim() || null,
        is_active: isActive,
        effective_start: start || null,
        effective_end: end || null,
      };
      if (editing) {
        await updatePlan(editing.id, payload);
        addToast("success", "Plan updated");
      } else {
        await createPlan(payload as Parameters<typeof createPlan>[0]);
        addToast("success", "Plan created");
      }
      onSaved();
    } catch {
      addToast("error", "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editing ? "Edit plan" : "Add benefit plan"}
      size="xl"
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Plan name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Blue Cross PPO Gold"
          />
          <Input
            label="Carrier"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            placeholder="Blue Cross Blue Shield"
          />
        </div>

        <Select
          label="Plan type"
          value={planType}
          onChange={(e) => setPlanType(e.target.value as PlanType)}
          options={PLAN_TYPES.map((t) => ({
            value: t.value,
            label: t.label,
          }))}
        />

        <div className="rounded-md border border-border/40 bg-background/40 p-3">
          <p className="text-xs font-medium text-text-muted mb-2">
            Coverage tiers — monthly premium per employee
          </p>
          <div className="space-y-2">
            {tierRates.map((r) => (
              <div
                key={r.tier}
                className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center"
              >
                <span className="text-sm text-text-primary">
                  {TIER_LABELS[r.tier]}
                </span>
                <Input
                  label="Employer pays"
                  type="number"
                  value={String(r.employer_cost)}
                  onChange={(e) =>
                    updateTier(r.tier, "employer_cost", e.target.value)
                  }
                />
                <Input
                  label="Employee pays"
                  type="number"
                  value={String(r.employee_cost)}
                  onChange={(e) =>
                    updateTier(r.tier, "employee_cost", e.target.value)
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Deductible"
            value={details.deductible}
            onChange={(e) =>
              setDetails((d) => ({ ...d, deductible: e.target.value }))
            }
            placeholder="$500"
          />
          <Input
            label="Out-of-pocket max"
            value={details.out_of_pocket_max}
            onChange={(e) =>
              setDetails((d) => ({
                ...d,
                out_of_pocket_max: e.target.value,
              }))
            }
            placeholder="$4,000"
          />
          <Input
            label="Copay"
            value={details.copay}
            onChange={(e) =>
              setDetails((d) => ({ ...d, copay: e.target.value }))
            }
            placeholder="$25 PCP / $50 specialist"
          />
          <Input
            label="Coinsurance"
            value={details.coinsurance}
            onChange={(e) =>
              setDetails((d) => ({ ...d, coinsurance: e.target.value }))
            }
            placeholder="80% after deductible"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Plan document name"
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
            placeholder="2026 Summary of Benefits.pdf"
          />
          <Input
            label="Plan document URL"
            value={docUrl}
            onChange={(e) => setDocUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Effective start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
          <Input
            label="Effective end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-border text-accent focus:ring-accent"
          />
          Active (available for selection)
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} isLoading={saving}>
            <Save className="h-4 w-4" />
            {editing ? "Save changes" : "Create plan"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default BenefitsPlansPage;
